import config from "@Config/index";
import axios from "axios";
import { fetchPayment } from "@Pages/Payment/utils";

export type PaymentVerificationResult = "success" | "failed" | "pending";

export type WechatPaymentStatusPayload = {
  state?: string | null;
  trade_state?: string | null;
  transaction_id?: string | null;
  query_success?: boolean;
  query_error?: string | null;
  queried_trade_state?: string | null;
};

const SUCCESS_STATES = new Set(["success", "succeeded"]);
const FAILURE_STATES = new Set(["failed", "cancelled"]);

const isSuccessState = (state?: string | null) => Boolean(state && SUCCESS_STATES.has(state));
const isFailureState = (state?: string | null) => Boolean(state && FAILURE_STATES.has(state));

export const fetchWechatPaymentStatus = async (
  token: string,
): Promise<WechatPaymentStatusPayload | null> => {
  try {
    const response = await axios.get(`${config.API_URL}/api/v0/payments/${token}/wechat_status`, {
      withCredentials: true,
    });
    return response?.data?.data ?? null;
  } catch {
    return null;
  }
};

/**
 * Confirms a payment is truly successful before showing /payments/complete.
 * For WeChat Pay, polls wechat_status (webhook state + query_order fallback).
 * For other gateways, polls GET /payments/:token.
 */
export const verifyPaymentCompletionStatus = async (
  token: string,
  payment?: { state?: string; gateway?: string } | null,
  options?: { maxAttempts?: number; intervalMs?: number },
): Promise<PaymentVerificationResult> => {
  const maxAttempts = options?.maxAttempts ?? 8;
  const intervalMs = options?.intervalMs ?? 2000;
  const isWechat = payment?.gateway === "wechat";

  let state: string | null | undefined = payment?.state;
  if (isSuccessState(state)) return "success";
  if (isFailureState(state)) return "failed";

  // Sequential polling by design — each attempt depends on the previous one.
  /* eslint-disable no-await-in-loop */
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    if (isWechat) {
      const statusPayload = await fetchWechatPaymentStatus(token);
      state = statusPayload?.state;
    } else {
      const paymentData = await fetchPayment(token);
      state = paymentData?.state;
    }

    if (isSuccessState(state)) return "success";
    if (isFailureState(state)) return "failed";
  }
  /* eslint-enable no-await-in-loop */

  return "pending";
};

export default verifyPaymentCompletionStatus;
